/**
 * Copy-to-clipboard with a fallback for non-secure contexts.
 *
 * ── Why a fallback is mandatory, not defensive ───────────────────────────────
 *
 * `navigator.clipboard` is gated on a SECURE CONTEXT. It is not merely
 * permission-gated — in an insecure context the whole `clipboard` property is
 * `undefined`, so `navigator.clipboard.writeText(...)` throws
 * `TypeError: Cannot read properties of undefined (reading 'writeText')`
 * before any promise exists to reject. A `try/catch` around an `await` does
 * catch it, which is exactly why this failed silently for so long.
 *
 * Only `https:` origins and the `localhost`/`127.0.0.1` loopback names are
 * potentially trustworthy. The Capacitor Android shell serves the bundle from
 * `http://localhost` (secure — loopback), so this works there. But pointed at
 * a LAN dev server (`http://10.198.251.201:3013`, `WAGEPRO_SHELL_DEV_SERVER`
 * in `apps/wagepro-mobile/capacitor.config.ts`) the origin is a plain-HTTP IP
 * — NOT loopback, NOT secure — and every copy button on the phone silently did
 * nothing. Confirmed from the device console, 2026-08-27, on the ESS
 * invite-link "Copy link" button.
 *
 * That is a dev-origin trigger, but the underlying fragility is not
 * dev-only: `writeText` also rejects when the call is not tied to a user
 * gesture, when the document is not focused, and in WebViews that ship the API
 * but refuse the write. A copy affordance whose only path is `writeText` is one
 * platform quirk away from being a dead button, and the caller has no way to
 * tell the user so.
 *
 * ── The fallback ────────────────────────────────────────────────────────────
 *
 * `document.execCommand('copy')` over a temporary off-screen `<textarea>`.
 * Deprecated, and deliberately used anyway: it is synchronous, needs no secure
 * context and no permission, and is the only copy mechanism that works on a
 * plain-HTTP origin. It is the fallback, never the first choice — `writeText`
 * is tried first wherever it exists.
 *
 * Returns a boolean rather than throwing so the caller can tell the user the
 * truth ("copied" vs. "select the link and copy it yourself") instead of
 * swallowing the failure. Never throws.
 */

/**
 * Result of a copy attempt. `false` means nothing reached the clipboard.
 *
 * `sourceElement`, when given, is the ALREADY-VISIBLE element displaying this
 * text (the invite-link box). Selecting a real, laid-out, on-screen element is
 * markedly more reliable in Android WebView than selecting a synthetic
 * off-screen `<textarea>`, which WebView often refuses to copy from — it is
 * tried before the synthetic node, and the synthetic node remains as the last
 * resort for callers that have no element to point at.
 */
export async function copyTextToClipboard(
  text: string,
  sourceElement?: HTMLElement | null,
): Promise<boolean> {
  // Preferred path: the async Clipboard API, where it exists AND the context
  // is secure enough to expose it. Guarded with `?.` because the property is
  // absent (not merely unusable) on an insecure origin.
  const asyncClipboard = navigator.clipboard as Clipboard | undefined;
  if (asyncClipboard !== undefined) {
    try {
      await asyncClipboard.writeText(text);
      return true;
    } catch (err) {
      // Fall through to the legacy path — a rejection here (no user gesture,
      // unfocused document, WebView refusal) does not mean copying is
      // impossible, only that this mechanism refused.
      console.warn(
        '[wagepro-web] clipboard: writeText refused, trying execCommand:',
        err,
      );
    }
  }

  if (sourceElement) {
    if (copyBySelectingElement(sourceElement)) return true;
  }

  return copyViaExecCommand(text);
}

/**
 * Select an existing on-screen element's text and copy the selection.
 *
 * The most WebView-friendly of the three paths: no synthetic node, nothing
 * hidden, and the selection is over content the engine has already laid out
 * and painted. Restores whatever the user had selected before.
 */
function copyBySelectingElement(element: HTMLElement): boolean {
  const legacyDocument: LegacyCopyDocument = document;
  const execCommand = legacyDocument.execCommand;
  if (typeof execCommand !== 'function') return false;

  const selection = window.getSelection();
  if (selection === null) return false;

  const previousRange =
    selection.rangeCount > 0 ? selection.getRangeAt(0).cloneRange() : null;

  try {
    const range = document.createRange();
    range.selectNodeContents(element);
    selection.removeAllRanges();
    selection.addRange(range);
    const copied = execCommand.call(legacyDocument, 'copy');
    if (!copied) {
      console.warn(
        '[wagepro-web] clipboard: execCommand(copy) over the visible element returned false',
      );
    }
    return copied;
  } catch (err) {
    console.warn(
      '[wagepro-web] clipboard: selecting the visible element failed:',
      err,
    );
    return false;
  } finally {
    selection.removeAllRanges();
    if (previousRange !== null) selection.addRange(previousRange);
  }
}

/**
 * Structural view of the one legacy API this module needs.
 *
 * `document` satisfies this by plain assignment — no cast, no `as unknown`.
 * Declared locally on purpose: `document.execCommand` resolves to the DOM
 * lib's `@deprecated` symbol, which `@typescript-eslint/no-deprecated`
 * (correctly) rejects, and this repo forbids `eslint-disable` comments
 * outright. Routing the single call through a type declared here states the
 * deliberate exception in code rather than suppressing the rule: the
 * deprecation is real and acknowledged in the module docblock above, and
 * `execCommand` remains the ONLY copy mechanism that works on a plain-HTTP
 * origin, where `navigator.clipboard` does not exist at all.
 *
 * Optional because the method is genuinely absent in some environments
 * (jsdom, for one).
 */
interface LegacyCopyDocument {
  execCommand?: (commandId: string) => boolean;
}

/**
 * Legacy synchronous copy. Works on insecure origins, which is the entire
 * point — see the module docblock.
 */
function copyViaExecCommand(text: string): boolean {
  // Probed rather than assumed, so "no mechanism available" is an ordinary
  // `false` return instead of a caught TypeError — that keeps a real copy
  // failure visible in the log instead of buried among expected ones.
  const legacyDocument: LegacyCopyDocument = document;
  const execCommand = legacyDocument.execCommand;
  if (typeof execCommand !== 'function') return false;

  const textarea = document.createElement('textarea');
  textarea.value = text;
  // Positioned off-screen at a REAL size rather than hidden at 1px/opacity:0.
  // `display:none`/`visibility:hidden` elements cannot be selected at all, and
  // a 1px transparent node is refused by some engines too — an off-screen node
  // with genuine dimensions is the recipe that actually copies. `readonly` is
  // deliberately NOT set: Android WebView will not select a readonly textarea.
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '-9999px';
  textarea.style.width = '2em';
  textarea.style.height = '2em';
  textarea.style.padding = '0';
  textarea.style.border = 'none';
  textarea.style.outline = 'none';
  textarea.style.boxShadow = 'none';
  textarea.style.background = 'transparent';
  document.body.appendChild(textarea);

  try {
    textarea.focus();
    textarea.select();
    // iOS Safari/WKWebView ignores `select()` on a readonly textarea unless
    // the range is set explicitly.
    textarea.setSelectionRange(0, text.length);
    const copied = execCommand.call(legacyDocument, 'copy');
    if (!copied) {
      // `execCommand` reports refusal by RETURN VALUE, not by throwing. Not
      // logging it was a real blind spot: a dead copy button produced no
      // console output whatsoever, so there was nothing to diagnose from.
      console.warn(
        '[wagepro-web] clipboard: execCommand(copy) over a synthetic textarea returned false',
      );
    }
    return copied;
  } catch (err) {
    console.error('[wagepro-web] clipboard: execCommand copy failed:', err);
    return false;
  } finally {
    textarea.remove();
  }
}
